import { useMemo } from 'react';

import { AnnouncementBar } from '@/components/AnnouncementBar';
import { HeaderSubPage } from '@/components/HeaderSubPage';
import { Loader } from '@/components/Loader';
import { Navbar } from '@/components/Navbar';
import { Paragraph } from '@/components/Paragraph';
import { useItineraryContext } from '@/context/ItineraryContext';
import { EnforcePageWidth } from '@/layout/PageLayout';
import { addListIdToObjectList } from '@/utils/addListIdToObjectList';

import { FlightPlans } from './FlightPlans';
import { LuggageOptions } from './LuggageOptions';
import { StyledFlightPlanBlock, StyledInfoBlock } from './styles';

import type { FlightOption } from '@/types/flights';

export function FlightsPage() {
  const { data, isLoading } = useItineraryContext();
  const flightPlans = data?.flightPlans;

  const selectedFlightPlans = useMemo(
    () => addListIdToObjectList(flightPlans?.filter((flightPlan) => flightPlan.selected) ?? []),
    [flightPlans],
  );

  const alternativeFlightPlans = useMemo(
    () => flightPlans?.flatMap((flightPlan) => (flightPlan.alternate ? [flightPlan.alternate] : [])),
    [flightPlans],
  );

  const mainFlightLuggage: FlightOption[] = useMemo(() => {
    const mainFlight = selectedFlightPlans.find((flightPlan) => flightPlan.mainFlight);
    const luggageOption = mainFlight?.flightOptions?.find((option) => option.type === 'luggage');
    const alternateFlightOptions = luggageOption?.alternate?.filter((option) => option.type === 'luggage');

    return [luggageOption, ...(alternateFlightOptions ?? [])].filter(Boolean) as FlightOption[];
  }, [selectedFlightPlans]);

  const mainFlightLuggageWithListId = useMemo(() => addListIdToObjectList(mainFlightLuggage), [mainFlightLuggage]);

  return (
    <>
      <AnnouncementBar />
      <Navbar variant="secondary" />
      <HeaderSubPage title="Your flights" emptyHeader />

      <EnforcePageWidth>
        <>
          <StyledFlightPlanBlock>
            {(isLoading || selectedFlightPlans.length > 0) && <h2>Selected flights</h2>}
            {isLoading && <Loader size="big" />}
            {selectedFlightPlans &&
              data &&
              selectedFlightPlans.map((flightPlan) => (
                <FlightPlans key={flightPlan.listId} itinerary={data} flightPlan={flightPlan} selected />
              ))}
          </StyledFlightPlanBlock>

          <StyledFlightPlanBlock>
            {(isLoading || alternativeFlightPlans.length > 0) && <h2>Alternative flights</h2>}
            {isLoading && <Loader size="big" />}
            {alternativeFlightPlans &&
              data &&
              alternativeFlightPlans.map((flightPlan) => (
                <FlightPlans key={flightPlan.listId} itinerary={data} flightPlan={flightPlan} selected={false} />
              ))}
          </StyledFlightPlanBlock>
          <h2>Luggage</h2>
          {mainFlightLuggageWithListId.length > 0 && (
            <StyledInfoBlock $padding="small">
              <LuggageOptions options={mainFlightLuggageWithListId} />
            </StyledInfoBlock>
          )}

          <StyledInfoBlock $padding="medium">
            <h2>We offset the CO2 emitted</h2>
            <Paragraph pt={4} size="lg">
              We take responsibility for the environmental impact of air travel by offsetting the CO2 emissions from
              your flight. Through certified carbon offset programs, we invest in sustainable projects that reduce
              greenhouse gases, such as reforestation, renewable energy, and conservation initiatives. Travel with peace
              of mind, knowing that your journey contributes to a greener future.
            </Paragraph>
          </StyledInfoBlock>
        </>
      </EnforcePageWidth>
    </>
  );
}
